import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useNavigate } from 'react-router-dom'
import Button from '../ui/Button'
import { pricingContent } from '../../data/landing-content'
import { startCheckout, type PlanId } from '../../lib/billing'

export default function PricingTiers() {
  const { isSignedIn, getToken } = useAuth()
  const navigate = useNavigate()
  const [busyPlan, setBusyPlan] = useState<PlanId | null>(null)

  const handleUpgrade = async (planId: PlanId) => {
    // Signed-out users sign up first (preserves original CTA behavior);
    // signed-in users go straight to Stripe Checkout.
    if (!isSignedIn) {
      navigate('/sign-up')
      return
    }
    try {
      setBusyPlan(planId)
      await startCheckout(getToken, planId)
    } catch (e: any) {
      alert(e.message || 'Could not start checkout')
      setBusyPlan(null)
    }
  }

  return (
    <section id="pricing" className="mt-22">
      <div className="mb-11 text-center">
        <h2 className="text-[clamp(2rem,2.8vw+1rem,2.8rem)] tracking-[-0.03em] font-bold m-0 mb-4">
          {pricingContent.sectionTitle}
        </h2>
        <p className="text-ink-soft leading-relaxed max-w-[520px] mx-auto m-0">
          {pricingContent.sectionDescription}
        </p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        {pricingContent.tiers.map((tier, idx) => {
          const planId = (tier as any).planId as PlanId | undefined
          const busy = busyPlan === planId
          return (
            <div
              key={idx}
              className={`
                rounded-card border px-8 py-9 bg-surface shadow-card flex flex-col gap-5
                ${tier.highlight ? 'border-2 border-[rgba(52,199,89,0.4)] -translate-y-4 bg-gradient-to-b from-[rgba(52,199,89,0.12)] to-[rgba(255,255,255,0.9)]' : 'border-[rgba(28,31,42,0.08)]'}
              `}
            >
              <h3 className="text-[1.35rem] font-bold m-0">{tier.name}</h3>

              <div className="text-[2.4rem] font-bold tracking-tight">
                {tier.price}
                <span className="text-base font-medium text-ink-soft">{tier.period}</span>
              </div>

              <p className="m-0 text-ink-soft">{tier.description}</p>

              <ul className="list-none p-0 m-0 flex flex-col gap-3 text-ink-soft">
                {tier.features.map((feature, fIdx) => (
                  <li key={fIdx} className="before:content-['✓'] before:text-accent before:mr-2.5 before:font-semibold">
                    {feature}
                  </li>
                ))}
              </ul>

              {planId ? (
                <Button
                  variant={tier.highlight ? 'primary' : 'secondary'}
                  onClick={() => handleUpgrade(planId)}
                  disabled={busy}
                  className="mt-auto"
                >
                  {busy ? 'Redirecting…' : tier.cta.text}
                </Button>
              ) : (
                <Button
                  {...(tier.cta as any)}
                  variant={tier.highlight ? 'primary' : 'secondary'}
                  className="mt-auto"
                >
                  {tier.cta.text}
                </Button>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
